import React from "react";
import { motion } from "framer-motion";
import {
  CheckCircle,
  ClipboardCheck,
  TrendingUp,
  Shield,
  Users,
  Target,
  Award,
  ArrowRight,
  AlertCircle,
  Lightbulb,
  Gauge,
  Lock,
  FileCheck,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const InternalAudits_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#5a2d7b] via-[#7a3fa0] to-[#9650c1] text-white py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-violet-400 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative max-w-4xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ClipboardCheck size={18} className="text-purple-300" />
            <span>Compliance and Operational Excellence</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-5">
            Internal Audits
          </h1>
          <p className="text-gray-100 text-base md:text-lg max-w-2xl mx-auto">
            Ensure operational effectiveness, regulatory compliance, and
            continuous improvement through systematic independent evaluation of
            your systems and processes.
          </p>
        </motion.div>
      </section>

      {/* What Is */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-6 text-gray-900">
              What Is an Internal Audit?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                An internal audit is an independent, systematic evaluation of an
                organization's operations, processes, and controls to ensure
                they are operating effectively, efficiently, and in compliance
                with organizational policies, standards, and regulatory
                requirements.
              </p>
              <p>
                Internal audits examine various aspects of performance
                including:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  "Compliance with internal policies and procedures",
                  "Adherence to relevant regulations and industry standards",
                  "Effectiveness of risk management processes",
                  "Operational efficiency and resource utilization",
                  "Financial controls and accuracy of reporting",
                  "Identification of process improvements and cost savings",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle
                      size={20}
                      className="text-purple-600 mt-0.5 flex-shrink-0"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-14 px-4 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Key Features of Internal Audits
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: "Compliance Verification",
                desc: "Ensures compliance with internal procedures, legal requirements, and industry standards.",
              },
              {
                icon: Target,
                title: "Process Evaluation",
                desc: "Assesses how business processes work, identifying inefficiencies and operational gaps.",
              },
              {
                icon: AlertCircle,
                title: "Risk Management Assessment",
                desc: "Evaluates organization's approach to managing financial, operational, and compliance risks.",
              },
              {
                icon: TrendingUp,
                title: "Performance Improvement",
                desc: "Provides insights into operational improvements and performance optimization opportunities.",
              },
              {
                icon: FileCheck,
                title: "Audit Trails and Evidence",
                desc: "Provides documented evidence of compliance and control effectiveness.",
              },
              {
                icon: Gauge,
                title: "Continuous Monitoring",
                desc: "Supports ongoing evaluation and improvement of organizational processes and systems.",
              },
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg hover:border-l-4 hover:border-purple-500 transition-all"
                >
                  <Icon size={32} className="text-purple-600 mb-3" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-8 text-gray-900">
              Why Internal Audits Matter for Your Business
            </h2>
            <div className="space-y-6">
              {[
                {
                  title: "Ensure Compliance",
                  desc: "Ensures compliance with regulations, standards, and policies reducing risk of fines and penalties.",
                },
                {
                  title: "Identify Operational Risks",
                  desc: "Evaluates processes to identify inefficiencies, resource wastage, and control weaknesses.",
                },
                {
                  title: "Support Decision Making",
                  desc: "Provides objective, data-driven insights enabling management to make informed decisions.",
                },
                {
                  title: "Enhance Transparency",
                  desc: "Provides independent review ensuring transparency in operations and stakeholder trust.",
                },
                {
                  title: "Facilitate Continuous Improvement",
                  desc: "Highlights improvement areas enabling continuous enhancement of operations.",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-4 p-4 bg-purple-50 rounded-lg border-l-4 border-purple-500"
                >
                  <Star
                    size={24}
                    className="text-purple-600 flex-shrink-0 mt-1"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">
                      {item.title}
                    </h3>
                    <p className="text-gray-700 text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 px-4 bg-gradient-to-br from-purple-50 to-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Benefits of Internal Audits
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: CheckCircle,
                title: "Improved Compliance",
                desc: "Ensures compliance with policies and regulations reducing legal risks.",
              },
              {
                icon: Zap,
                title: "Increased Efficiency",
                desc: "Identifies inefficiencies and streamlines processes improving productivity.",
              },
              {
                icon: Shield,
                title: "Risk Mitigation",
                desc: "Identifies risks enabling preventive corrective actions.",
              },
              {
                icon: Lightbulb,
                title: "Enhanced Controls",
                desc: "Improves internal controls ensuring responsible and ethical operations.",
              },
              {
                icon: TrendingUp,
                title: "Better Decision Making",
                desc: "Provides accurate insights empowering leadership to drive continuous improvement.",
              },
              {
                icon: Award,
                title: "Stakeholder Confidence",
                desc: "Demonstrates commitment to compliance and governance building stakeholder trust.",
              },
            ].map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all text-center"
                >
                  <Icon size={36} className="text-purple-600 mx-auto mb-3" />
                  <h3 className="font-bold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {benefit.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How to Conduct */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-10 text-gray-900">
              How to Conduct Internal Audits
            </h2>
            <div className="space-y-6">
              {[
                {
                  step: "1",
                  title: "Define Audit Objectives and Scope",
                  desc: "Clearly define objectives and outline scope ensuring all relevant areas are covered.",
                },
                {
                  step: "2",
                  title: "Prepare Audit Plan",
                  desc: "Develop detailed plan including timelines, resources, and assessment criteria.",
                },
                {
                  step: "3",
                  title: "Gather Data",
                  desc: "Collect necessary data through interviews, surveys, document reviews, and inspections.",
                },
                {
                  step: "4",
                  title: "Conduct Audit Evaluation",
                  desc: "Assess processes, systems, and controls identifying non-compliance and weaknesses.",
                },
                {
                  step: "5",
                  title: "Prepare Audit Report",
                  desc: "Compile findings highlighting issues, improvements, and recommended corrective actions.",
                },
                {
                  step: "6",
                  title: "Follow-Up on Corrective Actions",
                  desc: "Ensure corrective actions are taken and verify effectiveness during follow-up audits.",
                },
              ].map((process, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-6 p-6 bg-purple-50 rounded-lg"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">{process.step}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">
                      {process.title}
                    </h3>
                    <p className="text-gray-700">{process.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-14 px-4 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Industries Benefiting from Internal Audits
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Financial Services and Banking",
              "Healthcare and Pharmaceuticals",
              "Manufacturing and Industrial",
              "Information Technology",
              "Retail and Consumer Goods",
              "Hospitality and Tourism",
            ].map((industry, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="bg-white rounded-lg p-6 shadow-md border-l-4 border-purple-500 text-center"
              >
                <ClipboardCheck
                  size={32}
                  className="text-purple-600 mx-auto mb-3"
                />
                <h3 className="font-bold text-gray-900">{industry}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-[#5a2d7b] via-[#7a3fa0] to-[#9650c1] rounded-2xl p-10 text-white text-center shadow-lg"
          >
            <ClipboardCheck
              size={40}
              className="mx-auto mb-4 text-purple-300"
            />
            <h3 className="text-2xl font-bold mb-3">
              Why Choose MBA Solutions for Internal Audits?
            </h3>
            <p className="text-purple-100 mb-6 text-sm leading-relaxed">
              MBA Solutions offers expert internal audit services, helping
              businesses evaluate operations, identify risks, ensure compliance,
              and drive continuous improvement.
            </p>
            <div className="bg-white/10 rounded-lg p-4 mb-6 text-left space-y-2 text-sm">
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-purple-300 flex-shrink-0"
                />
                Audit Planning and Risk Assessment
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-purple-300 flex-shrink-0"
                />
                Compliance Audits (ISO, environmental, financial)
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-purple-300 flex-shrink-0"
                />
                Internal Control Evaluation
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-purple-300 flex-shrink-0"
                />
                Operational and Process Audits
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-purple-300 flex-shrink-0"
                />
                Follow-Up and Continuous Monitoring
              </p>
            </div>
            <div className="space-y-2 text-sm mb-6">
              <p>
                📧 Email:{" "}
                <a
                  href="mailto:atif@mbasolutions.co"
                  className="underline hover:text-purple-200"
                >
                  atif@mbasolutions.co
                </a>
              </p>
              <p>
                🌐 Website:{" "}
                <a
                  href="https://www.mbasolutions.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-purple-200"
                >
                  www.mbasolutions.co
                </a>
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-white text-purple-700 font-bold hover:bg-purple-50 transition-all"
            >
              Get Started <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

const Star = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <polygon points="12 2 15.09 9.26 23 9.26 17.19 14.31 19.29 21.74 12 16.69 4.71 21.74 6.81 14.31 1 9.27 8.91 9.26 12 2" />
  </svg>
);

export default InternalAudits_Details;
